import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { CSSProperties } from 'react';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { Rarity } from '#src/__generated__/globalTypes.ts';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { LinkBox, LinkOverlay } from '#src/atoms/navigation/Box/index.tsx';
import { LabelS } from '#src/atoms/typography/index.tsx';
import { ScarcityLabel } from '#src/components/collections/ScarcityLabel/index.tsx';
import { ANY_SPORT_MY_CLUB_SLUG_COLLECTIONS_COLLECTION } from '#src/constants/__generated__/routes.ts';
import { useSportContext } from '#src/contexts/sport/index.ts';
import { ensureHexColor, isLightColor } from '#src/lib/colors.ts';

import type { SimpleCollectionPreview_cardCollection } from './__generated__/index.graphql.ts';

const Root = styled(LinkBox)`
  background:
    linear-gradient(
      180deg,
      rgba(var(--c-rgb-black), 0.2),
      rgba(var(--c-rgb-black), 0.2)
    ),
    linear-gradient(
      180deg,
      var(--background-color) 0%,
      rgba(var(--c-rgb-black), 0.8) 100%
    );
  padding: var(--double-unit);
  border-radius: var(--double-unit);
  display: grid;
  grid-template-columns: calc(5 * var(--unit)) 1fr;
  column-gap: var(--double-unit);
  grid-template-areas:
    'picture rarityAndSeason'
    'picture teamName';
  color: var(--c-nd-700);

  &.withAdditionalInfos {
    grid-template-columns: calc(5 * var(--unit)) 1fr min-content;
    grid-template-areas:
      'picture rarityAndSeason additionalInfos'
      'picture teamName additionalInfos';
  }
`;
const StyledImg = styled.img`
  width: 40px;
  height: 40px;
  object-fit: contain;
  grid-area: picture;
`;
const RarityAndSeasonWrapper = styled.div`
  grid-area: rarityAndSeason;
  display: flex;
  gap: var(--half-unit);
`;
const TeamName = styled(LinkOverlay)`
  font: var(--t-16);
  grid-area: teamName;
`;

const AdditionalInfos = styled(Vertical).attrs({
  center: true,
})`
  grid-area: additionalInfos;
`;

type Props = {
  cardCollection: SimpleCollectionPreview_cardCollection | undefined;
  userId?: string;
  additionalInfos?: React.ReactNode;
};

export const SimpleCollectionPreview = ({
  cardCollection,
  userId,
  additionalInfos,
}: Props) => {
  const { generateSportContextPath } = useSportContext();

  if (!cardCollection) {
    return null;
  }

  const { team, season, rarity } = cardCollection;
  const { styleGuideColor } = team || {};

  const backgroundColor =
    styleGuideColor && !isLightColor(ensureHexColor(styleGuideColor))
      ? styleGuideColor
      : 'var(--c-nl-700-opaque)';

  if (!team || !season) return null;

  return (
    <Root
      className={classNames({
        withAdditionalInfos: !!additionalInfos,
      })}
      style={{ '--background-color': backgroundColor } as CSSProperties}
    >
      {!!team?.pictureUrl && <StyledImg src={team.pictureUrl} alt="" />}
      <RarityAndSeasonWrapper>
        <LabelS bold>
          <ScarcityLabel scarcity={rarity || Rarity.custom_series} />
        </LabelS>
        <LabelS bold>{season.name}</LabelS>
      </RarityAndSeasonWrapper>
      {userId ? (
        <TeamName
          as={Link}
          to={generateSportContextPath(
            ANY_SPORT_MY_CLUB_SLUG_COLLECTIONS_COLLECTION,
            {
              params: {
                slug: userId,
                collection: cardCollection.slug,
              },
            }
          )}
        >
          {team.name}
        </TeamName>
      ) : (
        <TeamName as="div">{team.name}</TeamName>
      )}
      {!!additionalInfos && (
        <AdditionalInfos>{additionalInfos}</AdditionalInfos>
      )}
    </Root>
  );
};

SimpleCollectionPreview.fragments = {
  cardCollection: gql`
    fragment SimpleCollectionPreview_cardCollection on CardCollection {
      id
      slug
      rarity
      team: anyTeam {
        slug
        name
        pictureUrl
        styleGuideColor
      }
      season {
        startYear
        name
      }
    }
  ` as TypedDocumentNode<SimpleCollectionPreview_cardCollection>,
};
